import { useEffect, useState } from "react";
import { DollarSign, ShoppingCart, Users, TrendingUp, Loader2, Upload, StickyNote, X } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api, { API, getToken, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge, STATUSES } from "@/components/OrderBits";
import { toast } from "sonner";

const COLORS = ["#0A3D62", "#00A89E", "#9CA986", "#0D4F7F", "#5FCFC7"];

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-6">
      <div className="flex items-center justify-between">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-accent"><Icon className="h-5 w-5 text-teal-dark" /></span>
      </div>
      <p className="mt-4 text-2xl font-bold text-ocean">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
}

function OrderManager({ order, onClose, onUpdate }) {
  const [status, setStatus] = useState(order.status);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const token = getToken();

  const saveStatus = async () => {
    setBusy(true);
    try { await api.patch(`/admin/orders/${order.id}/status`, { status }); toast.success("Status updated"); onUpdate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/admin/orders/${order.id}/notes`, { note }); setNote(""); toast.success("Note added"); onUpdate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const uploadReport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/orders/${order.id}/report`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Report uploaded — status set to Results Ready"); onUpdate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ocean-dark/40 p-4" data-testid="admin-order-modal" onClick={onClose}>
      <div className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-ocean">Order #{order.id.slice(0, 8).toUpperCase()}</h2>
            <p className="mt-1 text-sm text-slate-500">{order.username} · {new Date(order.created_at).toLocaleString()}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-ocean"><X className="h-5 w-5" /></button>
        </div>

        <div className="mt-5 rounded-2xl border border-border p-4">
          <p className="text-sm font-semibold text-ocean">Tests</p>
          {order.items.map((it, i) => (
            <p key={i} className="mt-1 text-sm text-slate-600">{it.tier_name}{it.supplement_name ? ` — ${it.supplement_name}` : ""} · ${it.price?.toFixed(2)}</p>
          ))}
          <p className="mt-2 text-sm font-bold text-ocean">Total: ${order.total?.toFixed(2)}{order.rush ? " · RUSH" : ""}</p>
        </div>

        {/* Status */}
        <div className="mt-5">
          <label className="text-sm font-semibold text-ocean">Update status</label>
          <div className="mt-2 flex gap-2">
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-status-select" className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm outline-none focus:border-teal">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={saveStatus} disabled={busy} data-testid="admin-status-save" className="rounded-xl bg-ocean px-5 py-2.5 text-sm font-semibold text-white hover:bg-ocean-light disabled:opacity-60">Save</button>
          </div>
        </div>

        {/* Photos */}
        {order.photos?.length > 0 && (
          <div className="mt-5">
            <p className="text-sm font-semibold text-ocean">Customer photos</p>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {order.photos.map((p) => (
                <a key={p.id} href={`${API}/files/${p.storage_path}?auth=${token}`} target="_blank" rel="noreferrer">
                  <img src={`${API}/files/${p.storage_path}?auth=${token}`} alt={p.filename} className="h-20 w-full rounded-lg border border-border object-cover" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Report upload */}
        <div className="mt-5">
          <label className="text-sm font-semibold text-ocean">Final report (PDF)</label>
          {order.report && <p className="mt-1 text-xs text-emerald-600">Uploaded: {order.report.filename}</p>}
          <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-teal/50 bg-accent/40 px-5 py-3 text-sm font-semibold text-teal-dark hover:bg-accent" data-testid="admin-upload-report">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload report PDF
            <input type="file" accept="application/pdf" className="hidden" onChange={uploadReport} />
          </label>
        </div>

        {/* Notes */}
        <div className="mt-5">
          <label className="text-sm font-semibold text-ocean">Internal notes</label>
          <div className="mt-2 space-y-2">
            {(order.internal_notes || []).map((n) => (
              <div key={n.id} className="rounded-lg bg-secondary p-3 text-sm text-slate-600"><StickyNote className="mr-1 inline h-3.5 w-3.5 text-slate-400" /> {n.note} <span className="text-xs text-slate-400">— {n.by}, {new Date(n.at).toLocaleDateString()}</span></div>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" data-testid="admin-note-input" className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm outline-none focus:border-teal" />
            <button onClick={addNote} data-testid="admin-note-add" className="rounded-xl border border-border px-5 py-2.5 text-sm font-semibold text-ocean hover:bg-secondary">Add</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [orders, setOrders] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selected, setSelected] = useState(null);

  const loadAll = () => {
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const refresh = () => {
    loadAll();
    if (selected) api.get(`/orders/${selected.id}`).then((r) => setSelected(r.data)).catch(() => {});
  };

  const statusData = analytics ? STATUSES.map((s) => ({ name: s, value: analytics.by_status[s] || 0 })) : [];
  const tierData = analytics ? Object.entries(analytics.by_tier).map(([name, value]) => ({ name, value })) : [];

  return (
    <div className="bg-background" data-testid="admin-page">
      <div className="mx-auto max-w-7xl px-6 py-12 md:px-10 md:py-16">
        <h1 className="text-3xl font-bold tracking-tight text-ocean">Admin Console</h1>
        <p className="mt-1 text-slate-600">Manage orders, customers and view analytics.</p>

        <Tabs defaultValue="orders" className="mt-8">
          <TabsList className="bg-white">
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="customers" data-testid="admin-tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
          </TabsList>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-6">
            <div className="overflow-hidden rounded-2xl border border-border bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-secondary text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr><th className="px-5 py-3">Order</th><th className="px-5 py-3">Customer</th><th className="px-5 py-3">Tests</th><th className="px-5 py-3">Total</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Date</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {orders === null && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>}
                  {orders && orders.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No orders yet.</td></tr>}
                  {orders && orders.map((o) => (
                    <tr key={o.id} onClick={() => setSelected(o)} data-testid={`admin-order-row-${o.id}`} className="cursor-pointer transition-colors hover:bg-secondary/60">
                      <td className="px-5 py-4 font-semibold text-ocean">#{o.id.slice(0, 8).toUpperCase()}</td>
                      <td className="px-5 py-4 text-slate-600">{o.username}</td>
                      <td className="px-5 py-4 text-slate-600">{o.items.length}</td>
                      <td className="px-5 py-4 font-semibold text-ocean">${o.total?.toFixed(2)}</td>
                      <td className="px-5 py-4"><StatusBadge status={o.status} /></td>
                      <td className="px-5 py-4 text-slate-500">{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* CUSTOMERS */}
          <TabsContent value="customers" className="mt-6">
            <div className="overflow-hidden rounded-2xl border border-border bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-secondary text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr><th className="px-5 py-3">Username</th><th className="px-5 py-3">Orders</th><th className="px-5 py-3">Total spent</th><th className="px-5 py-3">Joined</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {customers.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">No customers yet.</td></tr>}
                  {customers.map((c) => (
                    <tr key={c.id} data-testid={`admin-customer-${c.id}`}>
                      <td className="px-5 py-4 font-semibold text-ocean">{c.username}</td>
                      <td className="px-5 py-4 text-slate-600">{c.order_count}</td>
                      <td className="px-5 py-4 font-semibold text-ocean">${c.total_spent?.toFixed(2)}</td>
                      <td className="px-5 py-4 text-slate-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* ANALYTICS */}
          <TabsContent value="analytics" className="mt-6">
            {analytics && (
              <>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Stat icon={DollarSign} label="Total revenue" value={`$${analytics.revenue?.toFixed(2)}`} />
                  <Stat icon={ShoppingCart} label="Total orders" value={analytics.total_orders} />
                  <Stat icon={Users} label="Customers" value={analytics.total_customers} />
                  <Stat icon={TrendingUp} label="Avg order value" value={`$${analytics.avg_order_value?.toFixed(2)}`} />
                </div>
                <div className="mt-6 grid gap-6 lg:grid-cols-2">
                  <div className="rounded-2xl border border-border bg-white p-6">
                    <h3 className="font-semibold text-ocean">Orders by status</h3>
                    <div className="mt-4 h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={statusData}>
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="value" radius={[6, 6, 0, 0]}>{statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border bg-white p-6">
                    <h3 className="font-semibold text-ocean">Tests by tier</h3>
                    <div className="mt-4 h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={tierData}>
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="value" radius={[6, 6, 0, 0]}>{tierData.map((_, i) => <Cell key={i} fill={COLORS[(i + 1) % COLORS.length]} />)}</Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {selected && <OrderManager order={selected} onClose={() => setSelected(null)} onUpdate={refresh} />}
    </div>
  );
}
